import {DebouncedFunc, throttle} from "lodash";
import {makeObservable, observable, runInAction} from "mobx";

// A sticky boolean is a boolean that prefers to stay false.
// If it is ever false, and the user sets true, it must wait before true is actually set
// If it is ever pending true, and user sets false, it must never be true
// If it does turn true, and user sets false, and not enough time has passed, it must change after a delay
// If it does turn true, and user sets false, and enough time has passed, it will turn false immediately
export class StickyBooleanObservable {
  /**
   * Caution setting this value! Use setValue instead for proper behaviour
   */
  value: boolean;

  // Controls setting true value, after any appropriate delays
  // This is a throttled function
  // See https://www.geeksforgeeks.org/lodash-_-throttle-method/?ref=lbp
  private _setTrueDelegate: DebouncedFunc<() => void>;

  // Controls clearing _valueWasRecentlyToggledTrue flag, and sets false if appropriate
  // This is a throttled function
  // See https://www.geeksforgeeks.org/lodash-_-throttle-method/?ref=lbp
  private _setFalseDelegate: DebouncedFunc<() => void>;

  // Indicates that we must not set false, there is a "cool down"
  private _valueWasRecentlyToggledTrue = false;
  // Indicates that the user requested false during "recently turned true cool down"
  private _valueShouldBeFalseAfterDelay = false;

  // Optional callback which broadcasts a change in state
  callback?: () => void;

  constructor(
    value: boolean,
    setTrueDelay: number = 1000,
    setFalseDelay: number = 250,
    callback?: () => void
  ) {
    this.value = value;
    this.callback = callback;

    this._setTrueDelegate = throttle(
      () => {
        runInAction(() => {
          this.value = true;
        });
        this._valueWasRecentlyToggledTrue = true;
        callback?.();
        // Clear the "value was recently set true" flag ASAP
        this._setFalseDelegate();
      },
      setTrueDelay,
      {
        leading: false,
        trailing: true
      }
    );

    this._setFalseDelegate = throttle(
      () => {
        this._valueWasRecentlyToggledTrue = false;
        if (this._valueShouldBeFalseAfterDelay) {
          runInAction(() => {
            this.value = false;
          });
          this._valueShouldBeFalseAfterDelay = false;
          callback?.();
        }
      },
      setFalseDelay,
      {
        leading: false,
        trailing: true
      }
    );

    makeObservable(this, {value: observable});
  }

  /**
   * Cancel any pending value changes, and set the desired state
   * @param newValue The value to set
   */
  flushValue(newValue: boolean) {
    this.cancelAndResetFlags();
    runInAction(() => {
      this.value = newValue;
    });
  }

  // Stops any pending requests, and clears any flags
  private cancelAndResetFlags() {
    this._setFalseDelegate.cancel();
    this._setTrueDelegate.cancel();
    this._valueShouldBeFalseAfterDelay = false;
    this._valueWasRecentlyToggledTrue = false;
  }

  /**
   * Set the value, observing all relevant rules
   * @param newValue The desired value
   */
  setValue(newValue: boolean) {
    // Both values match
    if (this.value === newValue) {
      // Cancel any pending changes, value must not change
      this.cancelAndResetFlags();
    }

    // Value is true, and user wants to set false
    if (this.value && !newValue) {
      // Ensure it is safe to change
      if (this._valueWasRecentlyToggledTrue) {
        this._valueShouldBeFalseAfterDelay = true;
        this._setFalseDelegate();
      } else {
        runInAction(() => {
          this.value = false;
        });
        this.callback?.();
      }
    }

    // Value is false, and user wants to set true
    if (!this.value && newValue) {
      this._setTrueDelegate();
    }
  }
}
